import React, { useEffect, useMemo, useState } from "react";
import { Chart } from "primereact/chart";
import { SelectButton } from "primereact/selectbutton";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import { getDashboard, getDashboardContext } from "../service/AffariService";
import DashboardKpiCard from "./dashboard/DashboardKpiCard";
import DashboardSection from "./dashboard/DashboardSection";
import DashboardRanking from "./dashboard/DashboardRanking";
import DashboardFunnel from "./dashboard/DashboardFunnel";
import DashboardInsightDialog from "./dashboard/DashboardInsightDialog";
import "./dashboard/Dashboard.scss";

const PERIOD_OPTIONS = [
  { label: "Semanal", value: "SEMANAL" },
  { label: "Mensual", value: "MENSUAL" },
  { label: "Anual", value: "ANUAL" },
];

const CHANNEL_LABELS = {
  MAYORISTA: "Mayorista",
  RETAIL: "Retail",
};

const getChannelOptions = (contexto) => {
  if (!contexto?.usaCanales) return [];

  const scope = contexto.scope || {};
  const canales = scope.todosLosCanales
    ? ["MAYORISTA", "RETAIL"]
    : Array.isArray(scope.canalesPermitidos)
    ? scope.canalesPermitidos
    : [];

  return [...new Set(canales.map((canal) => String(canal || "").trim().toUpperCase()))]
    .filter((canal) => CHANNEL_LABELS[canal])
    .map((canal) => ({ label: CHANNEL_LABELS[canal], value: canal }));
};

const numberFormatter = new Intl.NumberFormat("es-CL", {
  maximumFractionDigits: 0,
});
const percentFormatter = new Intl.NumberFormat("es-CL", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const moneyFormatter = new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
  maximumFractionDigits: 0,
});

const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const formatPercent = (value) => `${percentFormatter.format(Number(value || 0))} %`;
const formatMoney = (value) => moneyFormatter.format(Number(value || 0));
const formatApiDate = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  const yyyy = value.getFullYear();
  const mm = String(value.getMonth() + 1).padStart(2, "0");
  const dd = String(value.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const parseApiDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const formatShortDate = (value) => {
  if (!value) return "Sin registros";
  const date = new Date(value);
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const Tablero = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState("MENSUAL");
  const [fecha, setFecha] = useState(new Date());
  const [canal, setCanal] = useState(null);
  const [dashboardContext, setDashboardContext] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    getDashboardContext({ periodo, fecha: formatApiDate(fecha) })
      .then((contexto) => {
        if (!active) return;

        setDashboardContext(contexto);
        const options = getChannelOptions(contexto);

        if (!contexto?.usaCanales) {
          setCanal("");
          return;
        }

        if (options.length === 0) {
          setError("Tu usuario no tiene canales habilitados para visualizar el tablero.");
          setLoading(false);
          return;
        }

        const resolvedCanal = contexto?.canal?.toUpperCase();
        const initialCanal = options.some((option) => option.value === resolvedCanal)
          ? resolvedCanal
          : options[0].value;
        setCanal(initialCanal);
      })
      .catch((err) => {
        console.error("Error cargando contexto del dashboard", err);
        if (active) {
          setError("No fue posible determinar los canales disponibles del tablero.");
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // El contexto de acceso/canales se resuelve al entrar al tablero.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dashboardContext || canal === null) return;
    if (dashboardContext.usaCanales && !canal) return;

    let active = true;
    setLoading(true);
    setError("");

    getDashboard({
      periodo,
      fecha: formatApiDate(fecha),
      canal: dashboardContext.usaCanales ? canal : undefined,
    })
      .then((data) => {
        if (active) setDashboard(data);
      })
      .catch((err) => {
        console.error("Error cargando dashboard", err);
        if (active) setError("No fue posible cargar los indicadores del tablero.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [periodo, fecha, canal, dashboardContext]);

  const contexto = useMemo(
    () => dashboard?.contexto || dashboardContext || {},
    [dashboard?.contexto, dashboardContext],
  );
  const channelOptions = useMemo(() => getChannelOptions(contexto), [contexto]);
  const resumen = dashboard?.resumen || {};
  const conversion = dashboard?.tasaCierre || {};
  const evolucion = useMemo(() => dashboard?.evolucion || [], [dashboard?.evolucion]);
  const metricas = contexto.metricasDisponibles || [];
  const hasMetric = (metric) => metricas.includes(metric);

  const periodoLabel = useMemo(() => {
    if (!contexto?.rango?.desde || !contexto?.rango?.hasta) return "";
    const desde = parseApiDate(contexto.rango.desde);
    const hasta = parseApiDate(contexto.rango.hasta);
    const formatter = new Intl.DateTimeFormat("es-CL", {
      day: periodo === "ANUAL" ? undefined : "2-digit",
      month: periodo === "ANUAL" ? undefined : "short",
      year: "numeric",
    });
    if (periodo === "ANUAL") return String(desde.getFullYear());
    return `${formatter.format(desde)} — ${formatter.format(hasta)}`;
  }, [contexto?.rango?.desde, contexto?.rango?.hasta, periodo]);

  const activityChartData = useMemo(
    () => ({
      labels: evolucion.map((item) => item.periodo),
      datasets: [
        {
          label: "Cotizaciones",
          data: evolucion.map((item) => item.cotizaciones || 0),
          backgroundColor: "rgba(37, 99, 235, 0.82)",
          borderRadius: 7,
          maxBarThickness: 34,
        },
        {
          label: "Notas de Venta",
          data: evolucion.map((item) => item.notasVenta || 0),
          backgroundColor: "rgba(16, 185, 129, 0.82)",
          borderRadius: 7,
          maxBarThickness: 34,
        },
      ],
    }),
    [evolucion],
  );

  const amountChartData = useMemo(
    () => ({
      labels: evolucion.map((item) => item.periodo),
      datasets: [
        {
          label: "Monto cotizado",
          data: evolucion.map((item) => item.montoCotizado || 0),
          borderColor: "#2563eb",
          backgroundColor: "rgba(37, 99, 235, 0.08)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: "Monto vendido",
          data: evolucion.map((item) => item.montoVendido || 0),
          borderColor: "#059669",
          backgroundColor: "rgba(5, 150, 105, 0.05)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
      ],
    }),
    [evolucion],
  );

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { usePointStyle: true, boxWidth: 8, padding: 18 },
        },
        tooltip: { padding: 12, cornerRadius: 8 },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0 } },
        y: { beginAtZero: true, grid: { color: "rgba(148, 163, 184, .16)" } },
      },
    }),
    [],
  );

  const moneyChartOptions = useMemo(
    () => ({
      ...chartOptions,
      scales: {
        x: chartOptions.scales.x,
        y: {
          beginAtZero: true,
          grid: { color: "rgba(148, 163, 184, .16)" },
          ticks: {
            callback: (value) => {
              const numeric = Number(value);
              if (Math.abs(numeric) >= 1_000_000_000)
                return `$${(numeric / 1_000_000_000).toFixed(1)}B`;
              if (Math.abs(numeric) >= 1_000_000)
                return `$${(numeric / 1_000_000).toFixed(0)}M`;
              if (Math.abs(numeric) >= 1_000) return `$${(numeric / 1_000).toFixed(0)}K`;
              return `$${numeric}`;
            },
          },
        },
      },
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          ...chartOptions.plugins.tooltip,
          callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.raw)}` },
        },
      },
    }),
    [chartOptions],
  );

  const conversionDoughnut = useMemo(() => {
    const converted = Number(resumen.conversionCohorte || 0);
    return {
      labels: ["Convertidas", "No convertidas"],
      datasets: [
        {
          data: [converted, Math.max(0, 100 - converted)],
          backgroundColor: ["#059669", "#e8edf4"],
          borderWidth: 0,
          cutout: "76%",
        },
      ],
    };
  }, [resumen.conversionCohorte]);

  const conversionOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
    }),
    [],
  );

  const goToReport = ({ variant = "COTIZACION", extraFilters = {} } = {}) => {
    navigate("/reporte-cotizacion", {
      state: {
        dashboardReport: {
          variant,
          fechaDesde: contexto?.rango?.desde,
          fechaHasta: contexto?.rango?.hasta,
          ...extraFilters,
        },
      },
    });
    setInsight(null);
  };

  const showInsight = (data) => setInsight(data);

  const kpis = [
    hasMetric("COTIZACIONES") && {
      title: "Cotizaciones generadas",
      value: formatNumber(resumen.cotizacionesGeneradas),
      subtitle: `Última: ${formatShortDate(resumen.ultimaCotizacion)}`,
      icon: "pi pi-file-edit",
      tone: "blue",
      onClick: () =>
        showInsight({
          title: "Cotizaciones generadas",
          value: formatNumber(resumen.cotizacionesGeneradas),
          description: "Cotizaciones creadas dentro del periodo seleccionado.",
          navigate: { variant: "COTIZACION" },
        }),
    },
    hasMetric("COTIZACIONES") && {
      title: "Cotizaciones vigentes",
      value: formatNumber(resumen.cotizacionesVigentes),
      subtitle: "Excluye padres reemplazados por recotización",
      icon: "pi pi-bolt",
      tone: "indigo",
      onClick: () =>
        showInsight({
          title: "Cotizaciones vigentes",
          value: formatNumber(resumen.cotizacionesVigentes),
          description:
            "Universo vigente del embudo, excluyendo cotizaciones padre que ya fueron reemplazadas por una recotización.",
          navigate: { variant: "COTIZACION" },
        }),
    },
    hasMetric("VENTAS") && {
      title: "Notas de Venta",
      value: formatNumber(resumen.notasVenta),
      subtitle: "Emitidas dentro del periodo",
      icon: "pi pi-check-circle",
      tone: "green",
      onClick: () =>
        showInsight({
          title: "Notas de Venta",
          value: formatNumber(resumen.notasVenta),
          description: "Cotizaciones convertidas a Nota de Venta según la fecha real de orden de venta.",
          navigate: { variant: "NOTAVENTA" },
        }),
    },
    hasMetric("CONVERSION_NOTA_VENTA") && {
      title: "Conversión a NV",
      value: formatPercent(resumen.conversionCohorte),
      subtitle: `${formatNumber(conversion.convertidas)} cotizaciones convertidas`,
      icon: "pi pi-chart-line",
      tone: "emerald",
      onClick: () =>
        showInsight({
          title: "Conversión a Nota de Venta",
          value: formatPercent(resumen.conversionCohorte),
          description:
            "Porcentaje de cotizaciones creadas en la cohorte seleccionada que poseen una Nota de Venta asociada.",
          details: [
            { label: "Cotizaciones de cohorte", value: formatNumber(conversion.cotizacionesGeneradas) },
            { label: "Convertidas", value: formatNumber(conversion.convertidas) },
          ],
        }),
    },
    hasMetric("VENTAS") && {
      title: "Monto cotizado",
      value: formatMoney(resumen.montoCotizado),
      subtitle: "Valor comercial cotizado",
      icon: "pi pi-wallet",
      tone: "cyan",
      onClick: () =>
        showInsight({
          title: "Monto cotizado",
          value: formatMoney(resumen.montoCotizado),
          description: "Suma del detalle de las cotizaciones creadas en el periodo.",
          navigate: { variant: "COTIZACION" },
        }),
    },
    hasMetric("VENTAS") && {
      title: "Monto vendido",
      value: formatMoney(resumen.montoVendido),
      subtitle: "Valor convertido a Nota de Venta",
      icon: "pi pi-dollar",
      tone: "green",
      onClick: () =>
        showInsight({
          title: "Monto vendido",
          value: formatMoney(resumen.montoVendido),
          description: "Monto asociado a cotizaciones convertidas dentro del periodo de venta.",
          navigate: { variant: "NOTAVENTA" },
        }),
    },
    hasMetric("COBERTURA") && {
      title: "Cobertura",
      value: formatPercent(resumen.cobertura),
      subtitle: "Clientes alcanzados por el scope actual",
      icon: "pi pi-users",
      tone: "amber",
      onClick: () =>
        showInsight({
          title: "Cobertura comercial",
          value: formatPercent(resumen.cobertura),
          description:
            "Porcentaje de clientes cubiertos por cotizaciones dentro del periodo y alcance comercial del usuario.",
        }),
    },
  ].filter(Boolean);

  const renderLoading = () => (
    <div className="dashboard-shell">
      <Skeleton height="7rem" borderRadius="18px" className="mb-3" />
      <div className="grid">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div className="col-12 md:col-6 xl:col-4" key={item}>
            <Skeleton height="9rem" borderRadius="16px" />
          </div>
        ))}
      </div>
      <div className="grid mt-1">
        <div className="col-12 xl:col-6"><Skeleton height="22rem" borderRadius="18px" /></div>
        <div className="col-12 xl:col-6"><Skeleton height="22rem" borderRadius="18px" /></div>
      </div>
    </div>
  );

  if (loading && !dashboard) return renderLoading();

  return (
    <div className="dashboard-shell">
      <header className="dashboard-hero">
        <div className="dashboard-hero__copy">
          <div className="dashboard-hero__eyebrow">Inteligencia comercial</div>
          <h1>Dashboard comercial</h1>
          <p>
            Una lectura ejecutiva del rendimiento de {contexto.empresa || "la operación"},
            basada en el alcance de datos de tu usuario.
          </p>
          <div className="dashboard-hero__meta">
            <span><i className="pi pi-calendar mr-2" />{periodoLabel}</span>
            <span><i className="pi pi-users mr-2" />{contexto.scope?.tipo === "EQUIPO" ? "Vista de equipo" : "Vista individual"}</span>
          </div>
        </div>
        <div className="dashboard-toolbar">
          {contexto.usaCanales && channelOptions.length > 0 && (
            <div className="dashboard-toolbar__control">
              <label>Canal</label>
              <SelectButton
                value={canal}
                options={channelOptions}
                onChange={(e) => e.value && setCanal(e.value)}
                optionLabel="label"
                optionValue="value"
                allowEmpty={false}
              />
            </div>
          )}
          <div className="dashboard-toolbar__control">
            <label>Periodo</label>
            <SelectButton
              value={periodo}
              options={PERIOD_OPTIONS}
              onChange={(e) => e.value && setPeriodo(e.value)}
              optionLabel="label"
              optionValue="value"
              allowEmpty={false}
            />
          </div>
          <div className="dashboard-toolbar__control dashboard-toolbar__date">
            <label>Fecha de referencia</label>
            <Calendar
              value={fecha}
              onChange={(e) => e.value && setFecha(e.value)}
              showIcon
              dateFormat="dd-mm-yy"
              maxDate={new Date()}
            />
          </div>
          <Button
            icon="pi pi-refresh"
            className="p-button-outlined dashboard-refresh"
            aria-label="Actualizar tablero"
            tooltip="Actualizar tablero"
            onClick={() => setFecha(new Date(fecha.getTime()))}
          />
        </div>
      </header>

      {error && <Message severity="error" text={error} className="w-full mb-3" />}

      <div className={`dashboard-content ${loading ? "dashboard-content--loading" : ""}`}>
        <div className="dashboard-kpi-grid">
          {kpis.map((kpi) => <DashboardKpiCard key={kpi.title} {...kpi} />)}
        </div>

        <div className="dashboard-grid dashboard-grid--2 mt-4">
          <DashboardSection
            title="Actividad comercial"
            subtitle="Cotizaciones generadas versus Notas de Venta"
          >
            <div className="dashboard-chart">
              <Chart className="dashboard-chart__canvas" type="bar" data={activityChartData} options={chartOptions} />
            </div>
          </DashboardSection>

          <DashboardSection
            title="Evolución monetaria"
            subtitle="Monto cotizado versus monto vendido"
          >
            <div className="dashboard-chart">
              <Chart className="dashboard-chart__canvas" type="line" data={amountChartData} options={moneyChartOptions} />
            </div>
          </DashboardSection>
        </div>

        <div className="dashboard-grid dashboard-grid--funnel mt-4">
          {hasMetric("EMBUDO") && (
            <DashboardSection
              title="Estado de cotizaciones"
              subtitle="Distribución del universo vigente del periodo"
            >
              <DashboardFunnel
                items={dashboard?.embudo || []}
                moneyFormatter={formatMoney}
                onSelect={(item) =>
                  showInsight({
                    title: item.estado,
                    value: `${formatNumber(item.cantidad)} cotizaciones`,
                    description: "Cotizaciones vigentes que actualmente se encuentran en este estado.",
                    details: [{ label: "Monto", value: formatMoney(item.monto) }],
                    navigate: { variant: "COTIZACION" },
                  })
                }
              />
            </DashboardSection>
          )}

          {hasMetric("CONVERSION_NOTA_VENTA") && (
            <DashboardSection
              title="Conversión a Nota de Venta"
              subtitle="Conversión de la cohorte de cotizaciones seleccionada"
              className="dashboard-conversion-panel"
            >
              <button
                type="button"
                className="dashboard-conversion"
                onClick={() =>
                  showInsight({
                    title: "Conversión a Nota de Venta",
                    value: formatPercent(resumen.conversionCohorte),
                    description:
                      "Mide cuántas cotizaciones creadas en el periodo terminaron asociadas a una Nota de Venta.",
                    details: [
                      { label: "Generadas", value: formatNumber(conversion.cotizacionesGeneradas) },
                      { label: "Convertidas", value: formatNumber(conversion.convertidas) },
                    ],
                  })
                }
              >
                <div className="dashboard-conversion__chart">
                  <Chart className="dashboard-conversion__canvas" type="doughnut" data={conversionDoughnut} options={conversionOptions} />
                  <div className="dashboard-conversion__center">
                    <strong>{formatPercent(resumen.conversionCohorte)}</strong>
                    <span>conversión</span>
                  </div>
                </div>
                <div className="dashboard-conversion__stats">
                  <div><span>Cotizaciones</span><strong>{formatNumber(conversion.cotizacionesGeneradas)}</strong></div>
                  <div><span>Convertidas</span><strong>{formatNumber(conversion.convertidas)}</strong></div>
                </div>
              </button>
            </DashboardSection>
          )}
        </div>

        <div className="dashboard-grid dashboard-grid--3 mt-4">
          {hasMetric("RANKING_PRODUCTOS") && (
            <DashboardSection title="Top productos" subtitle="Ordenados por monto cotizado">
              <DashboardRanking
                items={dashboard?.rankingProductos || []}
                moneyFormatter={formatMoney}
                quantityFormatter={formatNumber}
                onSelect={(item) =>
                  showInsight({
                    title: item.descripcion,
                    value: formatMoney(item.monto),
                    description: "Participación del producto en las cotizaciones del periodo.",
                    details: [{ label: "Cantidad", value: formatNumber(item.cantidad) }],
                    navigate: { variant: "COTIZACION", extraFilters: { productoCodigo: item.codigo } },
                  })
                }
              />
            </DashboardSection>
          )}

          {hasMetric("RANKING_FAMILIAS") && (
            <DashboardSection title="Top familias" subtitle="Concentración comercial por familia">
              <DashboardRanking
                items={dashboard?.rankingFamilias || []}
                moneyFormatter={formatMoney}
                quantityFormatter={formatNumber}
                onSelect={(item) =>
                  showInsight({
                    title: `Familia ${item.codigo}`,
                    value: formatMoney(item.monto),
                    description: "Monto cotizado acumulado por la familia comercial.",
                    details: [{ label: "Cantidad", value: formatNumber(item.cantidad) }],
                  })
                }
              />
            </DashboardSection>
          )}

          {hasMetric("RANKING_VENDEDORES") && (
            <DashboardSection title="Rendimiento del equipo" subtitle="Vendedores ordenados por monto cotizado">
              <DashboardRanking
                items={dashboard?.rankingVendedores || []}
                moneyFormatter={formatMoney}
                quantityFormatter={formatNumber}
                onSelect={(item) =>
                  showInsight({
                    title: item.descripcion,
                    value: formatMoney(item.monto),
                    description: "Rendimiento comercial del vendedor dentro del periodo seleccionado.",
                    details: [{ label: "Cotizaciones", value: formatNumber(item.cantidad) }],
                    navigate: {
                      variant: "COTIZACION",
                      extraFilters: { vendedor: { codigo: item.codigo, descripcion: item.descripcion } },
                    },
                  })
                }
              />
            </DashboardSection>
          )}
        </div>
      </div>

      <DashboardInsightDialog
        visible={Boolean(insight)}
        insight={insight}
        onHide={() => setInsight(null)}
        onNavigate={(target) => goToReport({
          variant: target.variant,
          extraFilters: target.extraFilters || insight?.navigate?.extraFilters || {},
        })}
      />
    </div>
  );
};

export default Tablero;
